import { useState } from "react";
import { toast } from "sonner";
import { Download, ExternalLink, EyeOff, RotateCcw, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import StatusPill from "@/components/common/StatusPill";
import { giorno, giornoOra } from "@/lib/crm";
import { apriVersione, erroreDocumenti, fraseEvento, pesoFile, scaricaVersione } from "@/lib/documenti";

/* Ogni riga del registro dice chi, cosa e quando. Il registro non si modifica:
   quello che si legge qui è quello che è successo. */
function Storico({ eventi }) {
  if (!eventi.length) return <p className="text-sm text-muted-foreground">Ancora niente.</p>;
  return (
    <ol className="grid gap-group" data-testid="documento-storico">
      {eventi.map((e) => (
        <li key={e.id} className="grid gap-micro border-l-2 border-border pl-group" data-evento={e.evento}>
          <p className="text-sm text-foreground">{fraseEvento(e)}</p>
          {e.nota ? <p className="break-words text-sm text-muted-foreground">«{e.nota}»</p> : null}
          <p className="text-xs tabular-nums text-muted-foreground">
            {giornoOra(e.created_at)}
            {e.nome_file && e.evento !== "tolto" && e.evento !== "rimesso" ? ` · ${e.nome_file}` : ""}
          </p>
        </li>
      ))}
    </ol>
  );
}

function Versione({ v, onApri, onScarica }) {
  return (
    <li className="flex min-h-11 items-center gap-group py-inside" data-testid={`versione-${v.versione}`} data-attuale={v.attuale}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-inside">
          <span className="text-sm font-medium text-foreground">Versione {v.versione}</span>
          {v.attuale ? (
            <StatusPill stato="ok">Attuale</StatusPill>
          ) : (
            <StatusPill stato="neutro">Superata</StatusPill>
          )}
        </div>
        <p className="mt-micro text-xs text-muted-foreground">
          {v.caricato_da_nome || "—"} · {giornoOra(v.created_at)}
          {v.dimensione ? <span className="tabular-nums"> · {pesoFile(v.dimensione)}</span> : null}
        </p>
      </div>
      <Button type="button" variant="ghost" size="icon" className="h-11 w-11" onClick={() => onApri(v)} aria-label={`Apri la versione ${v.versione}`}>
        <ExternalLink />
      </Button>
      <Button type="button" variant="ghost" size="icon" className="h-11 w-11" onClick={() => onScarica(v)} aria-label={`Scarica la versione ${v.versione}`}>
        <Download />
      </Button>
    </li>
  );
}

/**
 * Il dettaglio di un documento: la versione attuale in cima, poi le versioni e
 * lo storico. Stesso pannello laterale del dettaglio dell'appuntamento.
 */
export default function DettaglioDocumento({ documento, puoCaricare, puoRimettere, onChiudi, onVersioneNuova, onTogli, onCambiato }) {
  const [busy, setBusy] = useState(false);
  const doc = documento;
  const attuale = doc?.attuale;

  const apri = async (v) => {
    try {
      await apriVersione(v.id);
    } catch (err) {
      toast.error(erroreDocumenti(err));
    }
  };
  const scarica = async (v) => {
    try {
      await scaricaVersione(v.id);
    } catch (err) {
      toast.error(erroreDocumenti(err));
    }
  };
  const rimetti = async () => {
    setBusy(true);
    try {
      await api.post(`/documenti/${doc.id}/rimetti`);
      toast.success("Rimesso nell'archivio");
      onCambiato();
    } catch (err) {
      toast.error(erroreDocumenti(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={doc != null} onOpenChange={(v) => !v && onChiudi()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="documento-dettaglio">
        {doc ? (
          <div className="grid grid-cols-1 gap-card">
            <div className="grid gap-inside pr-card">
              <p className="text-xs font-medium text-muted-foreground">{doc.tipo_etichetta}</p>
              <SheetTitle className="break-words text-lg">{doc.nome}</SheetTitle>
              <SheetDescription>
                {attuale.versione > 1 ? `Versione ${attuale.versione}, caricata` : "Caricato"} da {attuale.caricato_da_nome || "—"} il{" "}
                {giorno(attuale.created_at)}
                {doc.dal_calendario ? " · dall'appuntamento" : ""}
              </SheetDescription>
            </div>

            {doc.tolto ? (
              <div className="grid gap-micro rounded-lg bg-warning-subtle px-group py-inside text-sm text-warning-foreground" data-testid="documento-tolto">
                <p className="font-medium">
                  Tolto dall'archivio da {doc.tolto.da_nome || "—"} il {giorno(doc.tolto.at)}
                </p>
                {doc.tolto.motivo ? <p className="break-words">«{doc.tolto.motivo}»</p> : null}
                <p>Il file c'è ancora: si apre e si scarica.{puoRimettere ? "" : " Può rimetterlo il titolare."}</p>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-inside">
              <Button type="button" className="h-11 sm:h-9" onClick={() => apri(attuale)} data-testid="documento-apri">
                <ExternalLink />
                Apri
              </Button>
              <Button type="button" variant="outline" className="h-11 sm:h-9" onClick={() => scarica(attuale)} data-testid="documento-scarica">
                <Download />
                Scarica
              </Button>
              {puoCaricare && !doc.tolto ? (
                <Button type="button" variant="outline" className="h-11 sm:h-9" onClick={() => onVersioneNuova(doc)} data-testid="documento-versione-nuova">
                  <Upload />
                  Versione nuova
                </Button>
              ) : null}
            </div>

            <section className="grid gap-inside" aria-labelledby="documento-versioni-titolo">
              <h3 id="documento-versioni-titolo" className="text-sm font-semibold text-foreground">
                Versioni
              </h3>
              <ul className="grid grid-cols-1 divide-y divide-border">
                {doc.versioni.map((v) => (
                  <Versione key={v.id} v={v} onApri={apri} onScarica={scarica} />
                ))}
              </ul>
            </section>

            <section className="grid gap-group" aria-labelledby="documento-storico-titolo">
              <h3 id="documento-storico-titolo" className="text-sm font-semibold text-foreground">
                Storico
              </h3>
              <Storico eventi={doc.eventi} />
            </section>

            {doc.tolto && puoRimettere ? (
              <div className="border-t border-border pt-group">
                <Button type="button" variant="outline" className="h-11 sm:h-9" disabled={busy} onClick={rimetti} data-testid="documento-rimetti">
                  <RotateCcw />
                  Rimetti nell'archivio
                </Button>
              </div>
            ) : null}
            {!doc.tolto && doc.puo_togliere ? (
              <div className="border-t border-border pt-group">
                <Button
                  type="button"
                  variant="ghost"
                  className="h-11 text-danger hover:bg-danger-subtle hover:text-danger-foreground sm:h-9"
                  onClick={() => onTogli(doc)}
                  data-testid="documento-togli"
                >
                  <EyeOff />
                  Togli dall'archivio
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
